import { ReactElement, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import { icon_minus, icon_plus } from '@gn-audio/icons';
import {
  ProductService,
  ProductWithAttributes,
  ServiceType,
  SFCCProductItem,
} from '@gn-audio/jss-core';

import { SiteFeature } from '../../../types/siteSettings';
import {
  ECommerceEventTypes,
  ECommerceItems,
  ECommercePayload,
} from '../../../types/trackingTypes';
import { useSiteSettings } from '../../../utils/contexts/SiteSettingsContext';
import { TrackingContext } from '../../../utils/contexts/TrackingContext';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { isNotNil } from '../../../utils/utils';
import { Button } from '../../Button/Button';
import { FeatureToggle } from '../../FeatureToggle/FeatureToggle';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { useBasket } from '../BasketContext/useBasket';
import { GiftWrapping } from '../GiftWrapping/GiftWrapping';
import { mapToBasketService } from '../GiftWrapping/giftWrappingUtils';
import Engraving from './Engraving';

import styles from './Products.module.css';

interface ProductProps {
  product: SFCCProductItem;
  productGroup: ProductWithAttributes | undefined;
  eCommerceItem: ECommerceItems[];
  services?: SFCCProductItem[];
}

const BasketProductItem = ({
  product,
  productGroup,
  eCommerceItem,
  services = [],
}: ProductProps): JSX.Element => {
  const { setQuantity, removeProduct, patchProduct } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);
  const { sfcc } = useSiteSettings();
  const { t } = useFallbackTranslation();
  const handleDecreaseProductNumber = () => {
    if (!product.item_id) return;
    setQuantity(product.item_id, product.product_id, product.quantity - 1);
    gtmEvent(eCommercePayloadQuantityChange);
  };
  const handleIncreaseProductNumber = () => {
    if (!product.item_id) return;
    setQuantity(product.item_id, product.product_id, product.quantity + 1);
    const payload = {
      ...eCommercePayloadQuantityChange,
      event: ECommerceEventTypes.add_to_cart,
    };
    gtmEvent(payload);
  };

  const eCommercePayload: ECommercePayload = useMemo(
    () => ({
      event: ECommerceEventTypes.remove_from_cart,
      ecommerce: {
        currency: sfcc?.currency || '',
        items: eCommerceItem,
      },
    }),
    [sfcc, eCommerceItem]
  );

  const eCommercePayloadQuantityChange: ECommercePayload = useMemo(
    () => ({
      ...eCommercePayload,
      ecommerce: {
        ...eCommercePayload.ecommerce,
        items: eCommerceItem.map((item) => ({
          ...item,
          quantity: 1,
          price:
            product.price_after_order_discount !== undefined
              ? String((product.price_after_order_discount / product.quantity).toFixed(2))
              : '',
        })),
      },
    }),
    [product, eCommercePayload, eCommerceItem]
  );

  const handleRemove = () => {
    if (!product.item_id) return;
    gtmEvent(eCommercePayload);
    removeProduct(product.item_id);
  };

  const existingGiftWrappingService = services?.find(
    (service) => service.c_serviceType === ServiceType.GiftWrapping
  );

  const hasGiftWrapping = !!existingGiftWrappingService;

  const handleRemoveGiftWrapping = () => {
    if (existingGiftWrappingService?.item_id) {
      removeProduct(existingGiftWrappingService.item_id);
    }
  };
  const handleAddGiftWrapping = (giftWrappingService: ProductService) => {
    if (!product.item_id) return;
    const newServices = [...services.map(mapToBasketService), giftWrappingService];
    patchProduct(product.item_id, product.quantity, newServices);
  };

  return (
    <div className={styles.border}>
      <div className={styles.product}>
        <img src={product.c_thumbnail_image_url} alt={product.product_name} />
        <div className={styles.right}>
          <div className={styles.text}>
            <p className={styles.name}>{product.item_text}</p>
            <p className={styles.id}>{product.product_id}</p>
            <p className={styles.price}>
              {product.c_price_formatted !== product.c_price_after_item_discount_formatted ? (
                <span className={styles.beforeDiscount}>{product.c_price_formatted}</span>
              ) : null}
              <span>{product.c_price_after_item_discount_formatted}</span>
            </p>
          </div>
          <div className={styles.quantityControls}>
            <div className="flex flex-1 flex-row items-center">
              <Button
                onClick={handleDecreaseProductNumber}
                variant="quaternary"
                size="s"
                aria-label={t('cart.DecreaseQuantity')}
                icon={<JabraIcon Icon={icon_minus} className={styles.icon} />}
              />

              <span className={styles.quantity}>{product.quantity}</span>
              <Button
                variant="quaternary"
                onClick={handleIncreaseProductNumber}
                size="s"
                aria-label={t('cart.IncreaseQuantity')}
                icon={<JabraIcon Icon={icon_plus} className={styles.icon} />}
              />
            </div>
            <button onClick={handleRemove} className="cursor-pointer text-[0.875rem] text-gray-800">
              <span>{t('cart.Remove')}</span>
            </button>
          </div>
        </div>
      </div>
      <Engraving product={product} />
      <FeatureToggle featureName={SiteFeature.SfccBasketGiftWrapping}>
        <GiftWrapping
          groupId={productGroup?.groupId}
          hasGiftWrapping={hasGiftWrapping}
          onAddGiftWrapping={handleAddGiftWrapping}
          onRemoveGiftWrapping={handleRemoveGiftWrapping}
        />
      </FeatureToggle>
    </div>
  );
};

const BasketProducts = (): ReactElement | null => {
  const { basket, busy, isBasketOpen, groups } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);
  const { sfcc } = useSiteSettings();
  const basketItems = useMemo(() => basket?.product_items ?? [], [basket]);
  const products = basketItems.filter((p) => !p.c_isService);

  const eCommercePayload: ECommercePayload = useMemo(
    () => ({
      event: ECommerceEventTypes.view_cart,
      ecommerce: {
        currency: sfcc?.currency || '',
        items: basketItems.map((item) => ({
          item_name: item?.item_text || '',
          item_id: item?.product_id || '',
          price: item.price_after_item_discount || '',
          item_brand:
            groups
              .find((group) => group.sku === item.product_id)
              ?.attributes?.find((attr) => attr.key === 'Product_Portfolio')?.value || '',
          item_category:
            groups
              .find((group) => group.sku === item.product_id)
              ?.attributes?.find((attr) => attr.key === 'Meta_Producttype')?.value || '',
          quantity: item.quantity,
          currency: sfcc?.currency || '',
        })),
      },
    }),
    [basketItems, groups, sfcc]
  );

  const isGtmBasketViewEventSent = useRef(false);
  useEffect(() => {
    const isECommercePayloadUpdated = !busy;
    if (isBasketOpen && isECommercePayloadUpdated && !isGtmBasketViewEventSent.current) {
      gtmEvent(eCommercePayload);
      isGtmBasketViewEventSent.current = true;
    } else if (!isBasketOpen) {
      isGtmBasketViewEventSent.current = false;
    }
  }, [isBasketOpen, busy, eCommercePayload, gtmEvent]);

  return (
    <div className={classNames(styles.container)}>
      {products.map((product) => {
        const services = product.c_serviceIDs
          ?.map((serviceId) => basketItems.find((itm) => itm.c_productLineItemID === serviceId))
          .filter(isNotNil);

        const productGroup = groups.find((p) => p.sku === product.product_id);
        return (
          <BasketProductItem
            key={product.item_id}
            product={product}
            services={services}
            productGroup={productGroup}
            eCommerceItem={eCommercePayload.ecommerce.items.filter(
              (item) => item.item_id === product.product_id
            )}
          />
        );
      })}
    </div>
  );
};

export default BasketProducts;
